/**
 * Wind speed is a magnitude, so it takes an ORDINAL single-hue ramp — not the
 * multi-hue Beaufort rainbow marine maps often use. Direction is encoded purely
 * as arrow rotation, which leaves color free to do exactly one job.
 *
 * Steps are taken from the documented blue sequential ramp (600/500/400/300/200/100).
 * The map renders on a dark surface, so the anchor is flipped: light wind sits
 * dark and near the surface, strong wind reads bright. The darkest step is bounded
 * by the ordinal light-end rule (nothing darker than step 600 on a dark surface).
 *
 * Six bands, not seven: the ramp's steps are unevenly spaced, and the best
 * possible 7-step subset tops out at ΔL 0.048 — under the 0.06 floor, so two
 * pairs would have been indistinguishable. Six clears it with ΔL ≥ 0.093.
 *
 * Validated (all checks pass):
 *   node scripts/validate_palette.js \
 *     "#184f95,#256abf,#3987e5,#6da7ec,#9ec5f4,#cde2fb" \
 *     --ordinal --mode dark --surface "#12100f"
 */
export interface Band {
  /** Lower bound in knots, inclusive. */
  from: number;
  /** Upper bound in knots, exclusive. Infinity on the last band. */
  to: number;
  color: string;
  label: string;
}

export const BANDS: Band[] = [
  { from: 0,  to: 5,        color: '#184f95', label: '0–5' },
  { from: 5,  to: 10,       color: '#256abf', label: '5–10' },
  { from: 10, to: 15,       color: '#3987e5', label: '10–15' },
  { from: 15, to: 20,       color: '#6da7ec', label: '15–20' },
  { from: 20, to: 25,       color: '#9ec5f4', label: '20–25' },
  { from: 25, to: Infinity, color: '#cde2fb', label: '25+' },
];

/** Shown for stations that are reporting nothing, so absence never reads as calm. */
export const NO_DATA_COLOR = '#4a4a47';

export function bandFor(speedKt: number): Band {
  return BANDS.find((b) => speedKt >= b.from && speedKt < b.to) ?? BANDS[BANDS.length - 1];
}

export function colorFor(speedKt: number | null | undefined): string {
  if (speedKt === null || speedKt === undefined || !Number.isFinite(speedKt)) {
    return NO_DATA_COLOR;
  }
  return bandFor(speedKt).color;
}

/**
 * A MapLibre `step` expression over the same bands, so the map layer and the
 * legend cannot drift apart.
 */
export function mapLibreStepExpression(property: string): unknown[] {
  const expr: unknown[] = ['step', ['get', property], BANDS[0].color];
  for (const b of BANDS.slice(1)) expr.push(b.from, b.color);
  return expr;
}
